import {
  WORK_RIGHTS_UK_FIELDS,
  WORK_RIGHTS_UK_VALUES,
} from '@/core/field-names';
import { filterWorkRightsToBeIncluded } from '@/store/composables/uk/right-to-work-rule';
import { isEmptyObject } from '@/lib/is-empty-object';

/**
 * @param field {Object}
 * @param field.name {String}
 * @param field.value {Any}
 * @param fieldByKey {Object} - Optional map of all fields by field name
 * @returns {Promise<string>}
 */
export function validateRightToWork(field, fieldByKey = null) {
  switch (field.name) {
    case WORK_RIGHTS_UK_FIELDS.RESIDENCY:
      return validateResidency(field);
    case WORK_RIGHTS_UK_FIELDS.SHARE_CODE:
      return validateShareCode(field, fieldByKey);
    case WORK_RIGHTS_UK_FIELDS.PASSPORT:
      return validatePassport(field);
    case WORK_RIGHTS_UK_FIELDS.BIRTH_CERTIFICATE:
      return validateBirthCertificate(field);
    case WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS:
      return validateSupportingDocuments(field);
    default:
      return '';
  }
}

function filterThatNeedsValidation(fields) {
  const fieldValueMap = fields.reduce((prev, field) => {
    return {
      ...prev,
      [field.name]: {
        value: field?.fileValue || field.value,
        required: field?.validation?.required,
        errorList: [],
      },
    };
  }, {});
  return filterWorkRightsToBeIncluded(fieldValueMap);
}

export function validateAllRightToWork(fields, changedField) {
  if (changedField?.name === WORK_RIGHTS_UK_FIELDS.RESIDENCY) {
    return null;
  }
  const fieldNamesToRunValidation = filterThatNeedsValidation(fields);

  // This will return Object that can access original
  // field object by field name for faster access.
  const fieldByKey = fields.reduce((prev, field) => {
    return {
      ...prev,
      [field.name]: {
        field: {
          ...field,
        },
      },
    };
  }, {});

  return fieldNamesToRunValidation.reduce((prev, name) => {
    return {
      ...prev,
      [name]: validateRightToWork(fieldByKey[name].field, fieldByKey),
    };
  }, {});
}

const ERRORS = {
  NON_SELECTED: 'Please select one',
  UNKNOWN_VALUE: 'Invalid input',
  REQUIRED_SHARE_CODE: 'A share code is required',
  INVALID_SHARE_CODE: 'Share code is invalid',
  EMPTY_PASSPORT: 'A passport is required',
  EMPTY_BIRTH_CERTIFICATE: 'A birth certificate is required',
  EXCEEDS_FILE_SIZE: 'File size exceeds 10MB',
};

export { ERRORS as WORK_RIGHT_ERRORS };

export function validateResidency(field) {
  if (!field?.value) {
    return ERRORS.NON_SELECTED;
  }
  if (field.value !== 'BritishIrish' && field.value !== 'Other') {
    return ERRORS.UNKNOWN_VALUE;
  }
  return '';
}

export function validateShareCode(field, fieldByKey = null) {
  const isOtherCountryResidence =
    fieldByKey?.[WORK_RIGHTS_UK_FIELDS.RESIDENCY]?.field?.value ===
    WORK_RIGHTS_UK_VALUES.OTHER_RESIDENCE;

  if (
    !field?.value ||
    (typeof field.value === 'string' && field.value.trim() === '')
  ) {
    if (isOtherCountryResidence) {
      return ERRORS.REQUIRED_SHARE_CODE;
    }
    return '';
  }

  if (typeof field.value !== 'string') {
    return ERRORS.INVALID_SHARE_CODE;
  }

  field.value = field.value.trim();
  const regex = /^[a-zA-Z0-9]{9}$/;
  if (!field.value.match(regex)) {
    return ERRORS.INVALID_SHARE_CODE;
  }
  return '';
}

// validation of file itself exist at component.
// TODO: Check whether this is still needed.
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
function validateFile(fileValue) {
  if (fileValue?.sizeInBytes >= MAX_FILE_SIZE) {
    return ERRORS.EXCEEDS_FILE_SIZE;
  }
  return '';
}

export function validatePassport(field) {
  if (!field.fileValue || isEmptyObject(field.fileValue)) {
    return ERRORS.EMPTY_PASSPORT;
  }

  return validateFile(field.fileValue);
}

export function validateBirthCertificate(field) {
  if (!field.fileValue || isEmptyObject(field.fileValue)) {
    return ERRORS.EMPTY_BIRTH_CERTIFICATE;
  }

  return validateFile(field.fileValue);
}

export function validateSupportingDocuments(field) {
  return validateFile(field.fileValue);
}
